import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  User,
  MapPin,
  DollarSign,
  Zap
} from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Quote {
  id: number;
  service: string;
  property_type: string;
  postcode: string;
  total_amount: number;
  status: string;
  created_at: string;
  customer_name: string;
}

interface ProviderQuote {
  id: number;
  provider_id: number;
  quote_amount: number;
  notes: string;
  status: string;
  created_at: string;
}

interface Provider {
  id: number;
  first_name: string;
  last_name: string;
  company_name: string;
  phone: string;
  email: string;
  rating: number;
}

interface QuoteProgress {
  quote: Quote;
  items: any[];
  provider_quotes: ProviderQuote[];
  quote_counts: {
    total: number;
    quoted: number;
    accepted: number;
    pending_provider_quotes: number;
  };
  providers: Provider[];
  progress: {
    status: string;
    submitted_at: string;
    updated_at: string;
    provider_responses: number;
  };
}

const QuoteProgressPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState<QuoteProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadQuoteProgress();

    // Auto-refresh every 5 seconds if quote is still pending
    const interval = autoRefresh ? setInterval(() => {
      loadQuoteProgress(true);
    }, 5000) : null;

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [id, autoRefresh]);

  const loadQuoteProgress = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setRefreshing(true);

      const response = await apiService.request(`/quotes/${id}/progress`, 'GET');

      if (response.success && response.data) {
        setData(response.data);
        setError(null);

        // Stop auto-refresh if quote is accepted
        if (response.data.quote.status === 'accepted') {
          setAutoRefresh(false);
          if (!silent) {
            toast({
              title: 'Quote Accepted!',
              description: 'Your quote request has been accepted by a provider.',
              variant: 'default'
            });
          }
        }
      } else {
        setError('Failed to load quote progress');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load quote progress');
      console.error('Error loading quote progress:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case 'quoted':
        return <Clock className="h-5 w-5 text-blue-600" />;
      case 'pending':
      case 'submitted':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-50 border-green-200';
      case 'quoted':
        return 'bg-blue-50 border-blue-200';
      case 'pending':
      case 'submitted':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-custom section-padding">
          <div className="text-center">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
            <p>Loading quote progress...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout>
        <div className="container-custom section-padding">
          <div className="text-center text-red-600">
            <AlertCircle className="h-8 w-8 mx-auto mb-4" />
            <p>{error || 'Quote not found'}</p>
            <Button onClick={() => navigate(-1)} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go Back
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  const { quote, provider_quotes, quote_counts, providers, progress } = data;

  return (
    <>
      <Helmet>
        <title>Quote Progress | Virewire</title>
        <meta name="description" content="Track your quote request progress and provider responses." />
      </Helmet>

      <Layout>
        <div className="container-custom section-padding">
          {/* Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-primary">Quote Progress</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadQuoteProgress()}
              disabled={refreshing}
              className="ml-auto"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Quote Info */}
            <div className="lg:col-span-2">
              {/* Quote Summary */}
              <Card className="shadow-card mb-6">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle>{quote.service}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">Quote Request Details</p>
                    </div>
                    <Badge 
                      variant={quote.status === 'accepted' ? 'default' : 'secondary'}
                      className="text-lg px-3 py-1"
                    >
                      {quote.status.charAt(0).toUpperCase() + quote.status.slice(1)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Property Type</p>
                      <p className="font-medium mt-1">{quote.property_type}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Location</p>
                      <p className="font-medium mt-1">{quote.postcode}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Your Estimate</p>
                      <p className="font-semibold text-primary text-lg mt-1">£{quote.total_amount.toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Submitted</p>
                      <p className="font-medium mt-1">{new Date(progress.submitted_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Progress Stats */}
              <Card className="shadow-card mb-6">
                <CardHeader>
                  <CardTitle className="text-lg">Provider Responses</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary">{quote_counts.total}</div>
                      <p className="text-sm text-muted-foreground mt-1">Total Responses</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600">{quote_counts.quoted}</div>
                      <p className="text-sm text-muted-foreground mt-1">With Quotes</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">{quote_counts.accepted}</div>
                      <p className="text-sm text-muted-foreground mt-1">Accepted</p>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-600">{quote_counts.pending_provider_quotes}</div>
                      <p className="text-sm text-muted-foreground mt-1">Pending</p>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-6">
                    <p className="text-sm font-medium mb-2">Response Rate</p>
                    <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                      <div
                        className="bg-gradient-to-r from-blue-500 to-green-500 h-full transition-all duration-500"
                        style={{
                          width: `${(quote_counts.quoted / Math.max(quote_counts.total, 1)) * 100}%`
                        }}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {quote_counts.total === 0 ? 'Waiting for provider responses...' : `${((quote_counts.quoted / quote_counts.total) * 100).toFixed(0)}% of providers have quoted`}
                    </p>
                  </div>

                  {/* Auto-refresh toggle */}
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-900">
                      <Zap className="inline h-4 w-4 mr-1 mb-0.5" />
                      Auto-refreshing every 5 seconds
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Provider Quotes List */}
              {provider_quotes.length > 0 && (
                <Card className="shadow-card">
                  <CardHeader>
                    <CardTitle className="text-lg">Provider Quotes</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {provider_quotes.map((pq) => {
                        const provider = providers.find(p => p.id === pq.provider_id);
                        return (
                          <div
                            key={pq.id}
                            className={`p-4 border rounded-lg transition-colors ${getStatusColor(pq.status)}`}
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {getStatusIcon(pq.status)}
                                <div>
                                  <p className="font-medium">
                                    {provider?.company_name || `Provider #${pq.provider_id}`}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    {provider?.first_name} {provider?.last_name}
                                  </p>
                                </div>
                              </div>
                              {pq.quote_amount > 0 && (
                                <div className="text-right">
                                  <p className="text-lg font-bold text-primary">£{pq.quote_amount.toFixed(2)}</p>
                                  <p className="text-xs text-muted-foreground">quoted price</p>
                                </div>
                              )}
                            </div>

                            {pq.notes && (
                              <p className="text-sm text-gray-700 mt-2 pl-6">
                                {pq.notes}
                              </p>
                            )}

                            <div className="flex items-center justify-between mt-2 pl-6">
                              <p className="text-xs text-muted-foreground">
                                {new Date(pq.created_at).toLocaleString()}
                              </p>
                              <Badge variant="outline" className="text-xs">
                                {pq.status.charAt(0).toUpperCase() + pq.status.slice(1)}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {provider_quotes.length === 0 && (
                <Card className="shadow-card">
                  <CardContent className="py-12">
                    <div className="text-center">
                      <Clock className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600">No provider responses yet</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Providers in your area are reviewing your request
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Quote Status Timeline */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className="w-3 h-3 rounded-full bg-green-600" />
                        <div className="w-0.5 h-8 bg-green-300" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Quote Submitted</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(progress.submitted_at).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${quote_counts.quoted > 0 ? 'bg-blue-600' : 'bg-gray-300'}`} />
                        <div className={`w-0.5 h-8 ${quote_counts.accepted > 0 ? 'bg-blue-300' : 'bg-gray-300'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">
                          {quote_counts.quoted > 0 ? 'Quotes Received' : 'Awaiting Quotes'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {quote_counts.quoted > 0 ? `${quote_counts.quoted} provider(s) responded` : 'Providers reviewing...'}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex flex-col items-center">
                        <div className={`w-3 h-3 rounded-full ${quote.status === 'accepted' ? 'bg-green-600' : 'bg-gray-300'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">Quote Accepted</p>
                        <p className="text-xs text-muted-foreground">
                          {quote.status === 'accepted' ? 'Complete' : 'Pending your selection'}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-lg">Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <Button className="w-full" variant="outline" onClick={() => loadQuoteProgress()}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh Now
                    </Button>
                    <Button className="w-full" variant="outline" onClick={() => navigate('/dashboard')}>
                      Back to Dashboard
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Info Card */}
              <Card className="shadow-card bg-blue-50 border-blue-200">
                <CardContent className="pt-6">
                  <p className="text-sm text-blue-900">
                    <strong>💡 Tip:</strong> Providers will review your request and submit their quotes shortly. You'll see updates as they arrive.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default QuoteProgressPage;
